import { getMeta } from './localdb.js';

/**
 * What the technician learns.
 *
 * The document so far is about what leaves the phone. Things arrive too, and the client has no
 * right to be surprised by them: a stop added at 10:02 for 10:30, a cancellation, a moved
 * window, an office note, a message from the customer.
 *
 * The five-minute heartbeat is far too slow for that, so while there is signal the client
 * **holds the WebSocket the portal already uses** and re-syncs on anything that names its own
 * day. Changes surface as a banner the technician acknowledges — *un arrêt a été ajouté à
 * 14 h* — never silently, so dispatch sees *vu par Marc 10:22* rather than wondering.
 *
 * Web Push comes later. The socket covers the case that matters: the technician who is online
 * and driving.
 */

export type FieldEvent =
  | { kind: 'assignment'; change: 'added' | 'moved' | 'cancelled' | 'note' | 'unassigned'; appointmentId: string; startsAt: string | null; customerName: string | null; at: string }
  | { kind: 'invalidate'; entity: string; id: string | null }
  | { kind: 'revoked' };

type Handler = (event: FieldEvent) => void;

let socket: WebSocket | null = null;
let retries = 0;
let closing = false;

/**
 * Connect, and keep connecting.
 *
 * The reconnect is deliberately unhurried and jittered: a fleet coming out of the same tunnel
 * at the same moment should not arrive at the server as one thundering herd, and the sync's
 * own heartbeat already covers the gap.
 */
export function connect(onEvent: Handler): () => void {
  closing = false;

  const open = async () => {
    if (closing) return;
    const userId = await getMeta('userId');
    if (!userId) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    try {
      socket = new WebSocket(`${protocol}//${window.location.host}/ws`);
    } catch {
      schedule();
      return;
    }

    socket.addEventListener('open', () => {
      retries = 0;
    });

    socket.addEventListener('message', (event) => {
      let message: Record<string, unknown>;
      try {
        message = JSON.parse(String(event.data)) as Record<string, unknown>;
      } catch {
        return;
      }

      if (message.type === 'assignment') {
        onEvent({
          kind: 'assignment',
          change: message.change as 'added' | 'moved' | 'cancelled' | 'note' | 'unassigned',
          appointmentId: String(message.appointmentId ?? ''),
          startsAt: (message.startsAt as string | null) ?? null,
          customerName: (message.customerName as string | null) ?? null,
          at: String(message.at ?? new Date().toISOString()),
        });
        return;
      }

      if (message.type === 'invalidate') {
        onEvent({ kind: 'invalidate', entity: String(message.entity), id: (message.id as string | null) ?? null });
      }
    });

    const reopen = () => {
      socket = null;
      schedule();
    };
    socket.addEventListener('close', (event) => {
      /*
       * 4401 is the server saying this socket is not authenticated — a revoked device, or a
       * session that ended. Reconnecting would be a loop, so it is reported instead and the
       * app decides: on a revoke, that means wiping the phone.
       */
      if (event.code === 4401) {
        onEvent({ kind: 'revoked' });
        return;
      }
      reopen();
    });
    socket.addEventListener('error', () => socket?.close());
  };

  const schedule = () => {
    if (closing) return;
    retries = Math.min(retries + 1, 6);
    const base = Math.min(2 ** retries * 1000, 60_000);
    window.setTimeout(() => void open(), base + Math.random() * 1000);
  };

  const onOnline = () => {
    if (!socket) void open();
  };
  window.addEventListener('online', onOnline);

  void open();

  return () => {
    closing = true;
    window.removeEventListener('online', onOnline);
    socket?.close();
    socket = null;
  };
}
